'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import AdSlot from '@/components/ads/AdSlot';
import type { ArchivedGalleryPreview } from './ThrowbackStrip';

export interface ThrowbackPhotoData {
  id: string;
  rank: number;
  celebrity_name: string;
  caption: string;
  image_url: string;
  credit: string;
  credit_html: string;
  license: string;
  license_url: string;
}

export interface ThrowbackGalleryData {
  id: string;
  theme: string;
  tagline: string;
}

interface Props {
  gallery: ThrowbackGalleryData;
  photos: ThrowbackPhotoData[];
  archivedGalleries?: ArchivedGalleryPreview[];
  onClose: () => void;
}

export default function ThrowbackModal({
  gallery: initialGallery,
  photos: initialPhotos,
  archivedGalleries = [],
  onClose,
}: Props) {
  const [activeGallery, setActiveGallery] = useState(initialGallery);
  const [activePhotos, setActivePhotos] = useState(initialPhotos);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [slideDir, setSlideDir] = useState<'next' | 'prev'>('next');

  const [galleryPool, setGalleryPool] = useState<ArchivedGalleryPreview[]>(() => {
    const initialPreview: ArchivedGalleryPreview = {
      gallery: initialGallery,
      thumbnail: initialPhotos[0]?.image_url || '',
      photoCount: initialPhotos.length,
    };
    const pool = [initialPreview, ...archivedGalleries];
    const seen = new Set<string>();
    return pool.filter((g) => {
      if (seen.has(g.gallery.id)) return false;
      seen.add(g.gallery.id);
      return true;
    });
  });

  const recommendations = galleryPool.filter((g) => g.gallery.id !== activeGallery.id);
  const hasMoreSlide = recommendations.length > 0;
  const totalSlides = activePhotos.length + (hasMoreSlide ? 1 : 0);
  const onMoreSlide = hasMoreSlide && current === activePhotos.length;

  const goNext = useCallback(() => {
    setCurrent((prev) => {
      if (prev >= totalSlides - 1) return prev;
      setSlideDir('next');
      setAnimating(true);
      setTimeout(() => setAnimating(false), 300);
      return prev + 1;
    });
  }, [totalSlides]);

  const goPrev = useCallback(() => {
    setCurrent((prev) => {
      if (prev <= 0) return prev;
      setSlideDir('prev');
      setAnimating(true);
      setTimeout(() => setAnimating(false), 300);
      return prev - 1;
    });
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, onClose]);

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) goNext();
    else goPrev();
  }, [goNext, goPrev]);

  const switchGallery = useCallback(async (galleryId: string) => {
    if (galleryId === activeGallery.id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/throwback/${galleryId}`);
      if (!res.ok) throw new Error('Failed to load gallery');
      const data = await res.json();
      const newPhotos = data.photos || [];
      setActiveGallery(data.gallery);
      setActivePhotos(newPhotos);
      setCurrent(0);
      setGalleryPool((prev) => {
        const exists = prev.some((g) => g.gallery.id === galleryId);
        if (exists) return prev;
        return [...prev, {
          gallery: data.gallery,
          thumbnail: newPhotos[0]?.image_url || '',
          photoCount: newPhotos.length,
        }];
      });
    } catch (err) {
      console.error('[ThrowbackModal] Failed to switch gallery:', err);
    } finally {
      setLoading(false);
    }
  }, [activeGallery.id]);

  if (activePhotos.length === 0) return null;

  const photo = onMoreSlide ? null : activePhotos[current];
  const showAd = !onMoreSlide && (current + 1) % 3 === 0 && current < activePhotos.length - 1;

  return (
    <div className="tb-modal-overlay" onClick={onClose}>
      <div className="tb-modal" onClick={(e) => e.stopPropagation()} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <button className="tb-modal-close" onClick={onClose} aria-label="Close gallery">
          ✕
        </button>

        <div className="tb-modal-header">
          <span className="tb-modal-label">CELEB THROWBACK</span>
          <span className="tb-modal-theme">{activeGallery.theme}</span>
        </div>

        {loading ? (
          <div className="tb-modal-loading">Loading gallery...</div>
        ) : onMoreSlide ? (
          <div className="tb-modal-more-slide">
            <div className="tb-modal-more-heading">MORE GALLERIES YOU&apos;LL LOVE</div>
            <div className="tb-modal-more-grid">
              {recommendations.map((a) => (
                <button
                  key={a.gallery.id}
                  className="tb-modal-more-card"
                  onClick={() => switchGallery(a.gallery.id)}
                  type="button"
                >
                  <Image
                    src={a.thumbnail}
                    alt={a.gallery.theme}
                    width={240}
                    height={160}
                    className="tb-modal-more-img"
                    style={{ objectFit: 'cover' }}
                  />
                  <div className="tb-modal-more-overlay">
                    <div className="tb-modal-more-theme">{a.gallery.theme}</div>
                    <div className="tb-modal-more-count">{a.photoCount} photos</div>
                  </div>
                </button>
              ))}
            </div>

            <div className="tb-modal-more-slide-ad">
              <AdSlot slot="throwback-more" width={728} height={90} format="rectangle" />
            </div>

            <button
              className="tb-modal-nav tb-modal-nav-prev tb-modal-more-back"
              onClick={goPrev}
              aria-label="Back to photos"
            >
              ‹ Back
            </button>
          </div>
        ) : (
          <>
            <div className="tb-modal-stage">
              <button
                className="tb-modal-nav tb-modal-nav-prev"
                onClick={goPrev}
                disabled={current === 0}
                aria-label="Previous photo"
              >
                ‹
              </button>

              <div
                className={`tb-modal-photo${animating ? ` tb-slide-${slideDir}` : ''}`}
                key={`photo-${current}`}
              >
                <Image
                  src={photo!.image_url}
                  alt={photo!.celebrity_name}
                  width={1200}
                  height={700}
                  className="tb-modal-img"
                  style={{ objectFit: 'contain' }}
                  priority
                />
              </div>

              <button
                className="tb-modal-nav tb-modal-nav-next"
                onClick={goNext}
                disabled={current === totalSlides - 1}
                aria-label="Next photo"
              >
                ›
              </button>
            </div>

            <div className={`tb-modal-info${animating ? ` tb-slide-${slideDir}` : ''}`} key={`info-${current}`}>
              <div className="tb-modal-name">{photo!.celebrity_name}</div>
              <div className="tb-modal-caption">{photo!.caption}</div>
              <div
                className="tb-modal-credit"
                dangerouslySetInnerHTML={{ __html: photo!.credit_html }}
              />
              <div className="tb-modal-counter">
                {current + 1} of {activePhotos.length}
              </div>
            </div>

            {showAd && (
              <div className="tb-modal-ad">
                <AdSlot slot="throwback-modal" width={728} height={90} format="rectangle" />
              </div>
            )}

            <div className="tb-modal-thumbs">
              {activePhotos.map((p, i) => (
                <button
                  key={p.id}
                  className={`tb-modal-thumb${i === current ? ' tb-modal-thumb-active' : ''}`}
                  onClick={() => setCurrent(i)}
                  aria-label={`View ${p.celebrity_name}`}
                >
                  <Image
                    src={p.image_url}
                    alt={p.celebrity_name}
                    width={80}
                    height={56}
                    className="tb-modal-thumb-img"
                    style={{ objectFit: 'cover' }}
                  />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
